import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Divider,
    Chip,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { ProductSearchAutocomplete } from "../common/ProductSearchAutocomplete";
import { productService } from "../../services/productService";
import { Product, Supplier } from "../../types";

export interface PurchaseOrderSearchParams {
    skip?: number;
    limit?: number;
    status?: string;
    supplier_id?: string;
    product_ids?: string[];
    date_from?: string;
    date_to?: string;
    expected_delivery_from?: string;
    expected_delivery_to?: string;
    reference_number?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
}

interface PurchaseOrderAdvancedSearchProps {
    open: boolean;
    onClose: () => void;
    onSearch: (params: PurchaseOrderSearchParams) => void;
    initialParams: PurchaseOrderSearchParams;
    suppliers: Supplier[];
}

export const PurchaseOrderAdvancedSearch: React.FC<
    PurchaseOrderAdvancedSearchProps
> = ({ open, onClose, onSearch, initialParams, suppliers }) => {
    const [searchParams, setSearchParams] =
        useState<PurchaseOrderSearchParams>(initialParams);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [activeFilterCount, setActiveFilterCount] = useState(0);

    useEffect(() => {
        setSearchParams(initialParams);

        const loadInitialData = async () => {
            // Load products
            if (
                initialParams.product_ids &&
                initialParams.product_ids.length > 0
            ) {
                try {
                    const promises = initialParams.product_ids.map((id) =>
                        productService.getProductById(id).catch(() => null),
                    );
                    const results = await Promise.all(promises);
                    setSelectedProducts(
                        results.filter((p): p is Product => p !== null),
                    );
                } catch (error) {
                    console.error("Failed to load products:", error);
                }
            } else {
                setSelectedProducts([]);
            }
        };

        if (open) {
            loadInitialData();
        }
    }, [initialParams, open]);

    useEffect(() => {
        // Count active filters
        let count = 0;
        if (searchParams.status) count++;
        if (searchParams.supplier_id) count++;
        if (searchParams.product_ids && searchParams.product_ids.length > 0)
            count++;
        if (searchParams.date_from || searchParams.date_to) count++;
        if (
            searchParams.expected_delivery_from ||
            searchParams.expected_delivery_to
        )
            count++;
        if (searchParams.reference_number) count++;
        setActiveFilterCount(count);
    }, [searchParams]);

    const handleChange = (
        field: keyof PurchaseOrderSearchParams,
        value: any,
    ) => {
        setSearchParams((prev) => ({
            ...prev,
            [field]: value === "" ? undefined : value,
        }));
    };

    const handleProductChange = (products: Product | Product[] | null) => {
        if (Array.isArray(products)) {
            setSelectedProducts(products);
            handleChange(
                "product_ids",
                products.length > 0
                    ? products.map((p) => p.product_id)
                    : undefined,
            );
        } else {
            setSelectedProducts(products ? [products] : []);
            handleChange(
                "product_ids",
                products ? [products.product_id] : undefined,
            );
        }
    };

    const handleSearch = () => {
        onSearch({
            ...searchParams,
            skip: 0, // Reset to first page
        });
        onClose();
    };

    const handleClear = () => {
        const clearedParams: PurchaseOrderSearchParams = {
            skip: 0,
            limit: searchParams.limit || 50,
            sort_by: "expected_delivery_date",
            sort_order: "desc",
        };
        setSearchParams(clearedParams);
        setSelectedProducts([]);
        onSearch(clearedParams);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <span>Advanced Purchase Order Search</span>
                    {activeFilterCount > 0 && (
                        <Chip
                            label={`${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} active`}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Status & Supplier Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Status & Supplier
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Order Status</InputLabel>
                                <Select
                                    value={searchParams.status || ""}
                                    label="Order Status"
                                    onChange={(e) =>
                                        handleChange("status", e.target.value)
                                    }
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                    <MenuItem value="CONFIRMED">
                                        Confirmed
                                    </MenuItem>
                                    <MenuItem value="RECEIVED">
                                        Received
                                    </MenuItem>
                                    <MenuItem value="PARTIAL_RECEIVED">
                                        Partial Received
                                    </MenuItem>
                                    <MenuItem value="CANCELLED">
                                        Cancelled
                                    </MenuItem>
                                    <MenuItem value="ON_HOLD">On Hold</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Supplier</InputLabel>
                                <Select
                                    value={searchParams.supplier_id || ""}
                                    label="Supplier"
                                    onChange={(e) =>
                                        handleChange(
                                            "supplier_id",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="">All Suppliers</MenuItem>
                                    {suppliers.map((supplier) => (
                                        <MenuItem
                                            key={supplier.supplier_id}
                                            value={supplier.supplier_id}
                                        >
                                            {supplier.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Product Filter */}
                    <Typography variant="subtitle2" gutterBottom>
                        Products
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <ProductSearchAutocomplete
                                multiple
                                value={selectedProducts}
                                onChange={handleProductChange}
                                label="Filter by Products (orders containing these products)"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Date Ranges */}
                    <Typography variant="subtitle2" gutterBottom>
                        Purchase Date Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={searchParams.date_from || ""}
                                onChange={(e) =>
                                    handleChange("date_from", e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.date_to || ""}
                                onChange={(e) =>
                                    handleChange("date_to", e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Expected Delivery Date Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={
                                    searchParams.expected_delivery_from || ""
                                }
                                onChange={(e) =>
                                    handleChange(
                                        "expected_delivery_from",
                                        e.target.value,
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.expected_delivery_to || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "expected_delivery_to",
                                        e.target.value,
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Reference Number */}
                    <Typography variant="subtitle2" gutterBottom>
                        Reference
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Reference Number"
                                value={searchParams.reference_number || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "reference_number",
                                        e.target.value,
                                    )
                                }
                                placeholder="Search by reference number..."
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Sorting */}
                    <Typography variant="subtitle2" gutterBottom>
                        Sort Results
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={
                                        searchParams.sort_by ||
                                        "expected_delivery_date"
                                    }
                                    label="Sort By"
                                    onChange={(e) =>
                                        handleChange("sort_by", e.target.value)
                                    }
                                >
                                    <MenuItem value="purchase_date">
                                        Purchase Date
                                    </MenuItem>
                                    <MenuItem value="expected_delivery_date">
                                        Expected Delivery
                                    </MenuItem>
                                    <MenuItem value="purchase_number">
                                        PO Number
                                    </MenuItem>
                                    <MenuItem value="total_amount">
                                        Total Amount
                                    </MenuItem>
                                    <MenuItem value="status">Status</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={searchParams.sort_order || "desc"}
                                    label="Sort Order"
                                    onChange={(e) =>
                                        handleChange(
                                            "sort_order",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleClear}
                    startIcon={<ClearIcon />}
                    color="warning"
                >
                    Clear All
                </Button>
                <Button
                    onClick={handleSearch}
                    variant="contained"
                    startIcon={<SearchIcon />}
                >
                    Search
                </Button>
            </DialogActions>
        </Dialog>
    );
};
